import { StyleSheet, Text, View } from 'react-native';
import type { DropsetVariant } from '@jackedlogs/shared';

import type { useTheme } from './theme';

// Read-only technique + cluster breakdown for a dropset / myorep row in the
// timeline feed. Static text only — no touch target: the interactive chain
// editors (DropsetBlock, Chip) are for authoring; this just SHOWS the chain the
// athlete already logged. IB-1 (Antenore 2026-07-03): a myorep's clusters were
// invisible in the feed because the row read only the (blank) parent — so the
// work looked uncounted. This surfaces the technique + steps under the
// top-round line.
//
//   • myorep     → steps repeat the top load (=top); reps alone communicate the
//                  cluster (Myo-rep · 10 · 9 · 5 · 4 · 4).
//   • classical  → each step drops the weight; shown as reps×weight when the
//                  step carries its own load (Drop · 10×80 · 6×60 kg).
//
// The technique WORD leads the line (never colour-only — it names the
// technique, and it de-crowds the row's top line where PR/regression/fail
// already compete). It carries the row's single a11y stop for the chain.
export function DropsetSummary({
  variant,
  steps,
  unit,
  t,
  theme,
}: {
  variant: DropsetVariant;
  steps: readonly { reps: string; weight: string }[];
  unit: 'kg' | 'lb';
  t: (k: string, opts?: Record<string, unknown>) => string;
  theme: ReturnType<typeof useTheme>;
}) {
  const styles = makeStyles(theme);
  const hasStepWeights =
    variant === 'classical' && steps.some((s) => s.weight.trim().length > 0);
  const parts = steps
    .map((s) => {
      const reps = s.reps.trim();
      if (!reps) return null;
      return hasStepWeights && s.weight.trim() ? `${reps}×${s.weight}` : reps;
    })
    .filter((p): p is string => p !== null);
  if (parts.length === 0) return null;
  const technique = t(
    variant === 'myorep'
      ? 'mobile.timeline.myorepTag'
      : 'mobile.timeline.dropsetTag',
  );
  const a11yTechnique = t(
    variant === 'myorep'
      ? 'mobile.timeline.myorepA11y'
      : 'mobile.timeline.dropsetA11y',
  );
  const clusters = `${parts.join(' · ')}${hasStepWeights ? ` ${unit}` : ''}`;
  return (
    <View style={styles.wrap}>
      {/* Single a11y node: the full-word technique + the reps, comma-read, so
          a screen reader announces "Myo-rep set: 10, 9, 5, 4, 4" once rather
          than spelling the abbreviation or fragmenting. No line cap — the
          clusters are logged data, never truncate them. */}
      <Text
        style={styles.text}
        accessibilityLabel={`${a11yTechnique}: ${parts.join(', ')}`}
      >
        <Text style={styles.technique}>{technique}</Text>
        {`  ${clusters}`}
      </Text>
    </View>
  );
}

function makeStyles(theme: ReturnType<typeof useTheme>) {
  const { palette: p, typography: ty } = theme;
  return StyleSheet.create({
    wrap: { marginTop: 2 },
    // Matches the reps/weight summary line one row up (ink2 · sm) — the
    // clusters are set data, not muted chrome, so they read at the same
    // weight as the set they extend.
    text: {
      color: p.ink2,
      fontSize: ty.sizes.sm,
      fontVariant: ['tabular-nums'],
    },
    technique: { fontWeight: '700' },
  });
}
